import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Grid,
  Paper,
  Text,
  Group,
  Avatar,
  Badge,
  Button,
  Tabs,
  Stack,
  Title,
  ThemeIcon,
  Timeline,
  ActionIcon,
  Menu,
  LoadingOverlay,
  Image,
  Select,
  Center,
  Skeleton,
  Modal,
  Table,
  Alert,
  SegmentedControl,
  Divider,
  Textarea,
  NumberInput,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  IconDeviceMobile,
  IconBrandApple,
  IconBrandAndroid,
  IconDotsVertical,
  IconBan,
  IconArrowBackUp,
  IconCheck,
  IconDownload,
  IconArrowLeft,
  IconDeviceWatch,
  IconListDetails,
  IconX,
  IconBrandSpotify,
  IconClock,
  IconAlertCircle,
  IconTrophy,
  IconTimeline,
  IconTable,
  IconLogout,
  IconUsersGroup,
} from "@tabler/icons-react";
import { Endpoints } from "../../../../lib/endpoints";
import { PerformRequest } from "../../../../hooks/usePerformRequest";
import { HttpCodes } from "../../../../lib/data";
import jsPDF from "jspdf";
import { NonPaginatedResponse } from "../../../../lib/types";
import { IUserProps, IDeviceProps } from "../../../../lib/types/User";
import { BadgeIcon } from "../../../../components/BadgeIcon";
import MemberVerificationControl from "../../../../components/MemberVerificationControl";

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState<IUserProps | null>(null);

  useEffect(() => {
    if (user?.firstName || user?.lastName) {
      const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
      document.title = `${name} | User Details | Virel Admin`;
    } else {
      document.title = "User Details | Virel Admin";
    }
  }, [user]);
  const [devices, setDevices] = useState<IDeviceProps[]>([]);
  const [squadData, setSquadData] = useState<any | null>(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [forceLogoutModalOpen, setForceLogoutModalOpen] = useState(false);
  const [forceLogoutLoading, setForceLogoutLoading] = useState(false);
  const [forceLogoutSuccess, setForceLogoutSuccess] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string | null>("overview");

  // Activity Log State
  const [activities, setActivities] = useState<any[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityType, setActivityType] = useState<string | null>("all");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [activityPage, setActivityPage] = useState(1);
  const [totalActivities, setTotalActivities] = useState(0);
  const [activityViewMode, setActivityViewMode] = useState<
    "timeline" | "table"
  >("timeline");

  // Spotify History State
  const [spotifyHistory, setSpotifyHistory] = useState<any[]>([]);
  const [spotifyHistoryLoading, setSpotifyHistoryLoading] = useState(false);
  const [spotifyNextCursor, setSpotifyNextCursor] = useState<string | null>(
    null,
  );
  const [spotifyStatus, setSpotifyStatus] = useState<{
    connected: boolean;
    scopes: string;
    expiresAt: string;
    connectedAt: string;
  } | null>(null);

  // Suspension State
  const [suspensions, setSuspensions] = useState<any[]>([]);
  const [suspensionLoading, setSuspensionLoading] = useState(false);
  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [unsuspendModalOpen, setUnsuspendModalOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [unsuspendReason, setUnsuspendReason] = useState("");
  const [suspendActionLoading, setSuspendActionLoading] = useState(false);

  // Manual clawback (deduct ₦ from a user; balance may go negative → owing)
  const [clawbackModalOpen, setClawbackModalOpen] = useState(false);
  const [clawbackAmount, setClawbackAmount] = useState<number | "">("");
  const [clawbackReason, setClawbackReason] = useState("");
  const [clawbackLoading, setClawbackLoading] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchData();
      fetchActivities(); // Initial fetch
    }
  }, [userId]);

  useEffect(() => {
    if (activeTab === "activity") {
      fetchActivities();
    }
    if (activeTab === "history") {
      fetchSpotifyStatus();
      fetchSpotifyHistory();
    }
    if (activeTab === "suspension") {
      fetchSuspensions();
    }
  }, [activeTab, activityPage, activityType, dateRange]);

  const fetchSpotifyStatus = async () => {
    if (!userId) return;
    try {
      const res = await PerformRequest<any>({
        route: Endpoints.GetUserSpotifyStatus(userId).route,
        method: "GET",
      });
      if (res.status === HttpCodes.Success) {
        setSpotifyStatus(res.data?.data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchSpotifyHistory = async (cursor?: string) => {
    if (!userId) return;
    setSpotifyHistoryLoading(true);
    try {
      const res = await PerformRequest<any>({
        route: Endpoints.GetUserSpotifyHistory(userId, {
          limit: 20,
          cursor,
        }).route,
        method: "GET",
      });
      // Backend can return Spotify history in two shapes:
      // 1) Legacy: data: [...tracks]
      // 2) Structured: data: { items: [...tracks], nextCursor: string | null }
      if (res.status === HttpCodes.Success) {
        const payload = res.data?.data;

        if (Array.isArray(payload)) {
          setSpotifyHistory(payload);
          setSpotifyNextCursor(null);
        } else {
          setSpotifyHistory(payload?.items || []);
          setSpotifyNextCursor(payload?.nextCursor || null);
        }
      } else {
        setSpotifyHistory([]);
        setSpotifyNextCursor(null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSpotifyHistoryLoading(false);
    }
  };

  const fetchActivities = async () => {
    if (!userId) return;
    setActivityLoading(true);
    try {
      const res = await PerformRequest<any>({
        route: Endpoints.GetUserActivities(userId, {
          page: activityPage,
          limit: 10,
          type: (activityType as "all" | "mission" | "withdrawal") || "all",
          startDate: dateRange[0] ? dateRange[0].toISOString() : undefined,
          endDate: dateRange[1] ? dateRange[1].toISOString() : undefined,
        }).route,
        method: "GET",
      });

      if (res.status === HttpCodes.Success) {
        // The API returns { data: { data: [], total: ... } }
        const responseData = res.data?.data;
        setActivities(
          Array.isArray(responseData?.data) ? responseData.data : [],
        );
        setTotalActivities(responseData?.total || 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setActivityLoading(false);
    }
  };

  const fetchSuspensions = async () => {
    if (!userId) return;
    setSuspensionLoading(true);
    try {
      const res = await PerformRequest<any>({
        route: Endpoints.GetUserSuspensionHistory(userId, {
          page: 1,
          limit: 50,
        }).route,
        method: "GET",
      });
      if (res.status === HttpCodes.Success) {
        const responseData = res.data?.data;
        setSuspensions(Array.isArray(responseData?.data) ? responseData.data : []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSuspensionLoading(false);
    }
  };

  const handleSuspendUser = async () => {
    if (!user || !suspendReason.trim()) return;

    setSuspendActionLoading(true);
    try {
      const endpoint = Endpoints.SuspendUser(user.id);
      const res = await PerformRequest({
        route: endpoint.route,
        method: endpoint.method,
        data: { reason: suspendReason.trim() },
      });

      if (res.status === HttpCodes.Success) {
        setSuspendModalOpen(false);
        setSuspendReason("");
        await fetchData();
        await fetchSuspensions();
      } else {
        setErrorMessage(res.message || "Failed to suspend user");
        setErrorModalOpen(true);
      }
    } catch (error) {
      console.error(error);
      setErrorMessage("Failed to suspend user");
      setErrorModalOpen(true);
    } finally {
      setSuspendActionLoading(false);
    }
  };

  const handleClawback = async () => {
    if (!user || !clawbackReason.trim()) return;
    const amt = Number(clawbackAmount);
    if (!(amt > 0)) return;

    setClawbackLoading(true);
    try {
      const endpoint = Endpoints.ClawbackUser();
      const res = await PerformRequest<{ clawed: number; newBalance: number }>({
        route: endpoint.route,
        method: endpoint.method,
        data: { userId: user.id, amount: amt, reason: clawbackReason.trim() },
      });
      if (res.status === HttpCodes.Success) {
        setClawbackModalOpen(false);
        setClawbackAmount("");
        setClawbackReason("");
        await fetchData();
      } else {
        setErrorMessage(res.message || "Failed to claw back");
        setErrorModalOpen(true);
      }
    } catch (error) {
      console.error(error);
      setErrorMessage("Failed to claw back");
      setErrorModalOpen(true);
    } finally {
      setClawbackLoading(false);
    }
  };

  const handleUnsuspendUser = async () => {
    if (!user) return;

    setSuspendActionLoading(true);
    try {
      const endpoint = Endpoints.ActivateUser(user.id);
      const res = await PerformRequest({
        route: endpoint.route,
        method: endpoint.method,
        data: unsuspendReason.trim()
          ? { reason: unsuspendReason.trim() }
          : undefined,
      });

      if (res.status === HttpCodes.Success) {
        setUnsuspendModalOpen(false);
        setUnsuspendReason("");
        await fetchData();
        await fetchSuspensions();
      } else {
        setErrorMessage(res.message || "Failed to unsuspend user");
        setErrorModalOpen(true);
      }
    } catch (error) {
      console.error(error);
      setErrorMessage("Failed to unsuspend user");
      setErrorModalOpen(true);
    } finally {
      setSuspendActionLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch User Profile
      const profileReq = await PerformRequest<NonPaginatedResponse<IUserProps>>(
        {
          route: Endpoints.GetUserProfile(userId!).route,
          method: "GET",
        },
      );

      // Fetch Devices
      const devicesReq = await PerformRequest<
        NonPaginatedResponse<IDeviceProps[]>
      >({
        route: Endpoints.GetUserDevices(userId!).route,
        method: "GET",
      });

      // Fetch Squad
      const squadReq = await PerformRequest<
        NonPaginatedResponse<any>
      >({
        route: Endpoints.GetUserSquad(userId!).route,
        method: "GET",
      });

      if (profileReq.status === HttpCodes.Success) {
        setUser(profileReq.data?.data || null);
      }

      if (devicesReq.status === HttpCodes.Success) {
        const deviceData = devicesReq.data?.data;
        setDevices(Array.isArray(deviceData) ? deviceData : []);
      }

      if (squadReq.status === HttpCodes.Success) {
        setSquadData(squadReq.data?.data || null);
      }
    } catch (error) {
      console.error("Error fetching user details", error);
    } finally {
      setLoading(false);
    }
  };

  const triggerForceLogoutRequest = async () => {
    if (!user) return;
    setForceLogoutLoading(true);
    setForceLogoutSuccess(null);

    try {
      const endpoint = Endpoints.ForceLogoutUser(user.id);
      const res = await PerformRequest({
        route: endpoint.route,
        method: endpoint.method,
      });

      if (res.status === HttpCodes.Success) {
        setForceLogoutSuccess(true);
        fetchData(); // Reload
      } else {
        setForceLogoutSuccess(false);
      }
    } catch (error) {
      console.error(error);
      setForceLogoutSuccess(false);
    } finally {
      setForceLogoutLoading(false);
    }
  };

  const handleForceLogout = () => {
    if (!user) return;
    setForceLogoutSuccess(null);
    setForceLogoutLoading(false);
    setForceLogoutModalOpen(true);
  };

  const handleExportPDF = async () => {
    if (!user || !userId) return;

    let exportActivities: any[] = [];
    let exportActivityTotal = 0;
    try {
      const res = await PerformRequest<any>({
        route: Endpoints.GetUserActivities(userId, {
          page: 1,
          limit: 100,
          type: "all",
        }).route,
        method: "GET",
      });
      if (res.status === HttpCodes.Success) {
        const responseData = res.data?.data;
        exportActivities = Array.isArray(responseData?.data)
          ? responseData.data
          : [];
        exportActivityTotal = responseData?.total ?? exportActivities.length;
      }
    } catch (e) {
      console.error("Failed to load activities for PDF", e);
    }

    const doc = new jsPDF();
    const pageBottom = 285;
    const labelX = 20;
    const valueX = 62;
    const maxTextWidth = 128;

    let y = 28;

    const ensureSpace = (lines: number, lineHeight = 6) => {
      const need = lines * lineHeight;
      if (y + need > pageBottom) {
        doc.addPage();
        y = 20;
      }
    };

    const addWrapped = (text: string, x: number, size = 10) => {
      doc.setFontSize(size);
      doc.setFont("helvetica", "normal");
      const lines = doc.splitTextToSize(text, maxTextWidth);
      for (const line of lines) {
        ensureSpace(1, 5);
        doc.text(line, x, y);
        y += 5;
      }
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
    };

    const addLine = (label: string, value: string) => {
      ensureSpace(1, 10);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text(`${label}:`, labelX, y);
      doc.setFont("helvetica", "normal");
      const valueLines = doc.splitTextToSize(value || "N/A", maxTextWidth);
      doc.text(valueLines[0] ?? "N/A", valueX, y);
      y += 6;
      for (let i = 1; i < valueLines.length; i++) {
        ensureSpace(1, 6);
        doc.text(valueLines[i], valueX, y);
        y += 6;
      }
      y += 3;
    };

    const addSection = (title: string) => {
      y += 6;
      ensureSpace(3, 8);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(0);
      doc.text(title, labelX, y);
      y += 2;
      doc.setLineWidth(0.3);
      doc.line(labelX, y, 190, y);
      y += 10;
      doc.setFontSize(11);
    };

    const fmtDate = (d: Date | string | undefined) =>
      d ? new Date(d).toLocaleString() : "N/A";
    const fmtYesNo = (v: boolean | undefined) =>
      v === true ? "Yes" : v === false ? "No" : "N/A";

    doc.setFontSize(20);
    doc.text("User Profile Report", 105, 16, { align: "center" });
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${new Date().toLocaleString()}`, 105, 23, {
      align: "center",
    });
    doc.setDrawColor(0);
    doc.line(10, 26, 200, 26);
    y = 35;

    addSection("Identity & account");
    addLine("User ID", user.id);
    addLine("Name", `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || "N/A");
    addLine("Username", user.username || "N/A");
    addLine("Email", user.email);
    addLine("Phone", user.phoneNumber || "N/A");
    addLine("Email verified", fmtYesNo(user.isEmailVerified));
    addLine("Phone added / verified flag", fmtYesNo(user.isPhoneVerified));
    addLine("Google sign-in linked", fmtYesNo(user.googleConnected));
    addLine(
      "Account status",
      user.isActive
        ? user.isSuspended
          ? "Active record, suspended"
          : "Active"
        : "Inactive",
    );
    addLine("Referral code", user.referralCode || "N/A");
    addLine("Invited by (user id)", user.invitedBy || "N/A");
    addLine("Joined", fmtDate(user.createdAt));
    addLine("Last login", user.lastLoginAt ? fmtDate(user.lastLoginAt) : "Never");
    if (user.emailOtpExpiresAt) {
      addLine("Email OTP window expires", fmtDate(user.emailOtpExpiresAt));
    }

    addSection("Engagement & progression");
    addLine("Level", String(user.level ?? 1));
    addLine("Points", String(user.points ?? 0));
    addLine("Current streak (days)", String(user.streakCount ?? 0));
    addLine("Longest streak (days)", String(user.longestStreak ?? 0));
    addLine(
      "Lifetime coins earned",
      (user.totalCoinsEarned ?? 0).toLocaleString(),
    );

    addSection("Wallet & payouts");
    addLine("Total balance (NGN)", `₦${(user.balance ?? 0).toLocaleString()}`);
    addLine(
      "Available for withdrawal (NGN)",
      `₦${(user.availableBalance ?? 0).toLocaleString()}`,
    );
    if (user.bankAccount) {
      addLine("Bank name", user.bankAccount.bankName);
      addLine("Account name", user.bankAccount.accountName);
      addLine("Account number", user.bankAccount.accountNumber);
      addLine("Bank code", user.bankAccount.bankCode || "N/A");
      addLine("Recipient code", user.bankAccount.recipientCode || "N/A");
    } else {
      addLine("Linked bank account", "None");
    }

    addSection("Monetization");
    const monLabels: Record<string, string> = {
      eligible: "Eligible",
      monetized: "Monetized",
      pending: "Pending review",
      none: "None",
      not_monetized: "Not monetized",
    };
    addLine(
      "Status",
      user.monetizationStatus
        ? monLabels[user.monetizationStatus] ?? user.monetizationStatus
        : "N/A",
    );
    if (user.monetizationProgress) {
      const mp = user.monetizationProgress;
      addLine(
        "Activities completed (progress)",
        String(mp.activitiesCompleted ?? 0),
      );
      addLine("Participation days", String(mp.participationDays ?? "N/A"));
      addLine("Last activity date", fmtDate(mp.lastActivityDate));
      addLine("Profile complete", fmtYesNo(mp.hasCompletedProfile));
      addLine("Social linked (IG & TikTok)", fmtYesNo(mp.socialLinked));
    }

    addSection("Spotify (metadata only — tokens not exported)");
    if (user.spotify) {
      addLine("Linked", "Yes");
      addLine("Token expires", fmtDate(user.spotify.expiresAt));
      addLine("Scopes", user.spotify.scope || "N/A");
      addLine("Connected at", fmtDate(user.spotify.connectedAt));
    } else {
      addLine("Linked", "No linked Spotify object on profile");
    }
    if (user.spotifyAuthState) {
      addLine("OAuth state present", "Yes (value omitted)");
    }
    if (user.spotifyAuthStateExpiresAt) {
      addLine("OAuth state expires", fmtDate(user.spotifyAuthStateExpiresAt));
    }
    if (user.spotifyAuthRedirectUri) {
      addLine("OAuth redirect URI", user.spotifyAuthRedirectUri);
    }

    addSection("Notifications");
    addLine(
      "Push tokens registered",
      user.pushTokens?.length
        ? String(user.pushTokens.length)
        : "0 (raw tokens not exported)",
    );

    addSection("Badges");
    if (user.badges?.length) {
      user.badges.forEach((badge: any, idx: number) => {
        ensureSpace(5, 8);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text(`${idx + 1}. ${badge.name ?? "Badge"}`, labelX, y);
        y += 7;
        doc.setFont("helvetica", "normal");
        if (badge.description) {
          addWrapped(String(badge.description), labelX, 9);
        }
        if (badge.icon) {
          addLine("Icon", String(badge.icon));
        }
        if (badge.id) {
          addLine("Badge ID", String(badge.id));
        }
        if (badge.earnedAt) {
          addLine("Earned at", fmtDate(badge.earnedAt));
        }
        y += 4;
      });
    } else {
      addLine("Badges", "None");
    }

    addSection("Registered devices");
    if (devices.length > 0) {
      devices.forEach((d, i) => {
        ensureSpace(6, 7);
        doc.setFont("helvetica", "bold");
        doc.text(`Device ${i + 1}`, labelX, y);
        y += 7;
        doc.setFont("helvetica", "normal");
        addLine("Device ID", d.deviceId);
        addLine("Maker / model", `${d.maker} ${d.deviceModel}`);
        addLine("OS", `${d.os} ${d.osVersion}`);
        addLine("App version", d.appVersion || "—");
        addLine("IP address", d.ipAddress || "N/A");
        addLine("Last login", fmtDate(d.lastLogin));
        addLine("Record created", fmtDate(d.createdAt));
        addLine("Record updated", fmtDate(d.updatedAt));
        y += 2;
      });
    } else {
      addLine("Devices", "No devices recorded.");
    }

    addSection("Activity log (latest)");
    if (exportActivities.length > 0) {
      addLine(
        "Note",
        exportActivityTotal > exportActivities.length
          ? `Showing ${exportActivities.length} of ${exportActivityTotal} total activities (most recent).`
          : `${exportActivities.length} entr${exportActivities.length === 1 ? "y" : "ies"}.`,
      );
      y += 2;
      exportActivities.forEach((item) => {
        const title =
          item.type === "mission"
            ? "Activity / mission"
            : item.type === "withdrawal"
              ? "Withdrawal"
              : String(item.type || "Event");
        ensureSpace(3, 6);
        doc.setFont("helvetica", "bold");
        doc.text(`${title}: ${item.title ?? ""}`, labelX, y);
        y += 6;
        doc.setFont("helvetica", "normal");
        if (item.details) {
          addWrapped(String(item.details), labelX, 9);
        }
        const parts: string[] = [fmtDate(item.date)];
        if (item.status) parts.push(`status: ${item.status}`);
        if (item.missionId) parts.push(`missionId: ${item.missionId}`);
        if (item.amount != null && item.amount > 0) {
          const cur = item.currency ? ` ${item.currency}` : "";
          parts.push(`amount: ${item.amount}${cur}`);
        }
        addWrapped(parts.join(" · "), labelX, 9);
        y += 2;
      });
    } else {
      addLine("Activities", "None returned for export.");
    }

    const safeName = `${user.firstName ?? "User"}_${user.lastName ?? ""}`.replace(
      /\s+/g,
      "_",
    );
    doc.save(`User_${safeName}.pdf`);
  };

  const isClickableMissionActivity = (activity: any) => {
    return Boolean(activity?.type === "mission" && activity?.missionId);
  };

  const getOpportunityActivityUrl = (activity: any) => {
    if (!activity?.missionId) return null;
    return `/d/missions/${activity.missionId}`;
  };

  const activityStatusBadgeColor = (status: string | undefined) => {
    if (status === "approved" || status === "completed") return "green";
    if (
      status === "rejected" ||
      status === "failed" ||
      status === "revoked"
    )
      return "red";
    return "yellow";
  };

  const formatActivityAmountDisplay = (item: any): string | null => {
    if (!(item.amount > 0)) return null;
    const prefix =
      item.currency === "NGN" || item.type === "withdrawal" ? "₦" : "";
    let s = prefix + item.amount.toLocaleString();
    if (item.currency && item.currency !== "NGN") {
      s += ` ${item.currency}`;
    } else if (item.type === "mission" && !item.currency) {
      s += " Pts";
    }
    return s;
  };

  const activityKindLabel = (item: any) =>
    item.type === "mission"
      ? "Activity"
      : item.type === "withdrawal"
        ? "Withdrawal"
        : String(item.type ?? "—");

  if (!user && !loading) {
    return (
      <Container>
        <Text>User not found</Text>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <LoadingOverlay visible={loading} />

      {/* Header */}
      <Group justify="space-between" mb="lg">
        <Group>
          <ActionIcon variant="light" onClick={() => navigate(-1)}>
            <IconArrowLeft size={18} />
          </ActionIcon>
          <Title order={2}>User Profile</Title>
        </Group>
        <Group>
          <Button
            leftSection={<IconDownload size={16} />}
            variant="default"
            onClick={handleExportPDF}
          >
            Export PDF
          </Button>
          {user && (
            <>
              <Button
                color="orange"
                leftSection={<IconLogout size={16} />}
                onClick={handleForceLogout}
              >
                Force Logout
              </Button>
            </>
          )}
        </Group>
      </Group>

      {user && (
        <Stack gap="lg">
          {/* Basic Info Card */}
          <Paper p="lg" radius="md" withBorder>
            <Grid>
              <Grid.Col span={2}>
                <Avatar
                  size={120}
                  radius={120}
                  mx="auto"
                  name={`${user.firstName} ${user.lastName}`}
                  color="initials"
                />
              </Grid.Col>
              <Grid.Col span={10}>
                <Group justify="space-between" align="flex-start">
                  <div>
                    <Title order={3}>
                      {user.firstName} {user.lastName}
                    </Title>
                    {user.username && (
                      <Text fw={500} size="sm" c="blue">
                        @{user.username}
                      </Text>
                    )}
                    <Text c="dimmed">{user.email}</Text>
                    <Text c="dimmed">{user.phoneNumber}</Text>
                    <Group mt="xs">
                      <Badge color={user.isActive ? "green" : "gray"}>
                        {user.isActive ? "Active" : "Inactive"}
                      </Badge>
                      {user.isSuspended && <Badge color="red">Suspended</Badge>}
                      <Badge
                        color="yellow"
                        variant="filled"
                        leftSection={<IconTrophy size={14} />}
                      >
                        Level {user.level ?? 1}
                      </Badge>
                      {user.monetizationStatus === "monetized" && (
                        <Badge
                          variant="gradient"
                          gradient={{ from: "teal", to: "lime", deg: 105 }}
                        >
                          Monetized
                        </Badge>
                      )}
                    </Group>
                  </div>
                  <Stack align="flex-end" gap={4}>
                    <Text size="sm" c="dimmed">
                      Referral Code:{" "}
                      <Text span fw={500} c="dark">
                        {user.referralCode}
                      </Text>
                    </Text>
                    <Text size="sm" c="dimmed">
                      Joined: {new Date(user.createdAt).toLocaleDateString()}
                    </Text>
                    <Text size="sm" c="dimmed">
                      Last Login:{" "}
                      {user.lastLoginAt
                        ? new Date(user.lastLoginAt).toLocaleString()
                        : "Never"}
                    </Text>
                  </Stack>
                </Group>
              </Grid.Col>
            </Grid>
          </Paper>

          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Tab value="overview">Overview</Tabs.Tab>
              <Tabs.Tab value="devices">Devices & Security</Tabs.Tab>
              <Tabs.Tab value="activity">Activity</Tabs.Tab>
              <Tabs.Tab value="financials">Financials</Tabs.Tab>
              <Tabs.Tab value="badges">Badges</Tabs.Tab>
              <Tabs.Tab value="squad">Squad</Tabs.Tab>
              <Tabs.Tab value="suspension">Suspension</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="overview" pt="md">
              <Grid>
                <Grid.Col span={4}>
                  <Paper p="md" withBorder radius="md">
                    <Title order={4} mb="md">
                      Statistics
                    </Title>
                    {/* Placeholder stats */}
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Text size="sm">Current Streak</Text>
                        <Text fw={500}>{user.streakCount || 0}</Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm">Lifetime Earnings (Coins)</Text>
                        <Text fw={500}>
                          {user.totalCoinsEarned
                            ? user.totalCoinsEarned.toLocaleString()
                            : 0}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm">Level</Text>
                        <Text fw={500}>{user.level ?? 1}</Text>
                      </Group>
                    </Stack>
                  </Paper>
                </Grid.Col>
                <Grid.Col span={12}>
                  <MemberVerificationControl userId={userId!} />
                </Grid.Col>
              </Grid>
            </Tabs.Panel>

            <Tabs.Panel value="devices" pt="md">
              <Paper p="md" withBorder radius="md">
                <Title order={4} mb="md">
                  Login Devices
                </Title>
                {devices.length === 0 ? (
                  <Text c="dimmed">No device history found.</Text>
                ) : (
                  <Stack>
                    {devices.map((device) => (
                      <Paper key={device._id} p="sm" withBorder bg="var(--bg-surface-2)">
                        <Group>
                          <ThemeIcon size="lg" color="gray" variant="light">
                            {device.os === "iOS" || device.maker === "Apple" ? (
                              <IconBrandApple />
                            ) : (
                              <IconBrandAndroid />
                            )}
                          </ThemeIcon>
                          <div style={{ flex: 1 }}>
                            <Text fw={500}>
                              {device.maker} {device.deviceModel}
                            </Text>
                            <Text size="xs" c="dimmed">
                              OS: {device.os} {device.osVersion} • App: {device.appVersion || "—"} • IP:{" "}
                              {device.ipAddress}
                            </Text>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <Text size="sm">
                              {new Date(device.lastLogin).toLocaleString()}
                            </Text>
                            <Text size="xs" c="dimmed">
                              Last Seen
                            </Text>
                          </div>
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Tabs.Panel>

            <Tabs.Panel value="activity" pt="md">
              <Paper p="md" withBorder radius="md">
                <Group
                  justify="space-between"
                  mb="lg"
                  wrap="wrap"
                  align="flex-end"
                  gap="sm"
                >
                  <Title order={4}>Activity Log</Title>
                  <Group wrap="wrap">
                    <SegmentedControl
                      value={activityViewMode}
                      onChange={(v) =>
                        setActivityViewMode(v as "timeline" | "table")
                      }
                      data={[
                        {
                          label: (
                            <Group gap={6} wrap="nowrap" justify="center">
                              <IconTimeline size={16} />
                              <span>Timeline</span>
                            </Group>
                          ),
                          value: "timeline",
                        },
                        {
                          label: (
                            <Group gap={6} wrap="nowrap" justify="center">
                              <IconTable size={16} />
                              <span>Table</span>
                            </Group>
                          ),
                          value: "table",
                        },
                      ]}
                      size="sm"
                    />
                    <Select
                      placeholder="Type"
                      data={[
                        { value: "all", label: "All Activities" },
                        { value: "mission", label: "Activities" },
                        { value: "withdrawal", label: "Withdrawals" },
                      ]}
                      value={activityType}
                      onChange={setActivityType}
                      w={150}
                    />
                    <DatePickerInput
                      type="range"
                      placeholder="Pick dates"
                      value={dateRange}
                      onChange={(val) =>
                        setDateRange(val as [Date | null, Date | null])
                      }
                      w={220}
                      clearable
                    />
                  </Group>
                </Group>

                <div style={{ position: "relative", minHeight: 100 }}>
                  <LoadingOverlay
                    visible={activityLoading}
                    zIndex={100}
                    overlayProps={{ radius: "sm", blur: 2 }}
                  />

                  {activityLoading && activities.length === 0 ? (
                    <Stack gap="md" mt="md">
                      {[1, 2, 3].map((i) => (
                        <Group key={i} align="flex-start">
                          <Skeleton height={24} circle mb="xl" />
                          <div style={{ flex: 1 }}>
                            <Skeleton
                              height={16}
                              width="40%"
                              radius="xl"
                              mb={6}
                            />
                            <Skeleton height={12} width="80%" radius="xl" />
                          </div>
                        </Group>
                      ))}
                    </Stack>
                  ) : activities.length === 0 ? (
                    <Center py={60}>
                      <Stack align="center" gap="xs">
                        <ThemeIcon
                          size={60}
                          radius={60}
                          color="gray"
                          variant="light"
                        >
                          <IconListDetails size={30} />
                        </ThemeIcon>
                        <Text size="lg" fw={500} c="dimmed">
                          No activities found
                        </Text>
                        <Text size="sm" c="dimmed" ta="center" maw={300}>
                          There are no activities recorded for this user in the
                          selected period.
                        </Text>
                      </Stack>
                    </Center>
                  ) : activityViewMode === "table" ? (
                    <Table.ScrollContainer minWidth={720}>
                      <Table striped highlightOnHover verticalSpacing="sm">
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Type</Table.Th>
                            <Table.Th>Title</Table.Th>
                            <Table.Th>Details</Table.Th>
                            <Table.Th>Amount</Table.Th>
                            <Table.Th>Date</Table.Th>
                            <Table.Th>Status</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {activities.map((item) => {
                            const missionUrl =
                              isClickableMissionActivity(item) &&
                              getOpportunityActivityUrl(item);
                            const amountStr =
                              formatActivityAmountDisplay(item);
                            return (
                              <Table.Tr key={item.id}>
                                <Table.Td style={{ whiteSpace: "nowrap" }}>
                                  <Text size="sm">
                                    {activityKindLabel(item)}
                                  </Text>
                                </Table.Td>
                                <Table.Td maw={280}>
                                  {missionUrl ? (
                                    <Text
                                      component="a"
                                      href={missionUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      size="sm"
                                      c="blue"
                                      style={{
                                        textDecoration: "underline",
                                      }}
                                    >
                                      {item.title}
                                    </Text>
                                  ) : (
                                    <Text size="sm">{item.title}</Text>
                                  )}
                                </Table.Td>
                                <Table.Td maw={260}>
                                  <Text
                                    size="xs"
                                    c="dimmed"
                                    lineClamp={3}
                                    title={
                                      item.details
                                        ? String(item.details)
                                        : undefined
                                    }
                                  >
                                    {item.details ?? "—"}
                                  </Text>
                                </Table.Td>
                                <Table.Td style={{ whiteSpace: "nowrap" }}>
                                  {amountStr ? (
                                    <Text size="sm" fw={500} c="green">
                                      {amountStr}
                                    </Text>
                                  ) : (
                                    <Text size="sm" c="dimmed">
                                      —
                                    </Text>
                                  )}
                                </Table.Td>
                                <Table.Td style={{ whiteSpace: "nowrap" }}>
                                  <Text size="xs" c="dimmed">
                                    {new Date(item.date).toLocaleString()}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Badge
                                    size="xs"
                                    variant="light"
                                    color={activityStatusBadgeColor(
                                      item.status,
                                    )}
                                  >
                                    {item.status}
                                  </Badge>
                                </Table.Td>
                              </Table.Tr>
                            );
                          })}
                        </Table.Tbody>
                      </Table>
                    </Table.ScrollContainer>
                  ) : (
                    <Timeline active={-1} bulletSize={24} lineWidth={2}>
                      {activities.map((item) => {
                        const amountStr =
                          formatActivityAmountDisplay(item);
                        return (
                          <Timeline.Item
                            key={item.id}
                            bullet={
                              item.type === "mission" ? (
                                <IconCheck size={12} />
                              ) : (
                                <IconDownload size={12} />
                              )
                            }
                            title={
                              <Text size="sm" fw={500}>
                                {item.type === "mission"
                                  ? "Activity Completed"
                                  : "Withdrawal Request"}
                              </Text>
                            }
                          >
                            {isClickableMissionActivity(item) &&
                            getOpportunityActivityUrl(item) ? (
                              <Text
                                component="a"
                                href={getOpportunityActivityUrl(item)!}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="sm"
                                mt={4}
                                c="blue"
                                style={{ textDecoration: "underline" }}
                              >
                                {item.title}
                              </Text>
                            ) : (
                              <Text size="sm" mt={4}>
                                {item.title}
                              </Text>
                            )}
                            {item.details && (
                              <Text size="xs" c="dimmed">
                                {item.details}
                              </Text>
                            )}
                            {amountStr && (
                              <Text size="sm" fw={500} c="green">
                                {amountStr}
                              </Text>
                            )}
                            <Text size="xs" c="dimmed" mt={4}>
                              {new Date(item.date).toLocaleString()} •{" "}
                              <Badge
                                size="xs"
                                variant="light"
                                color={activityStatusBadgeColor(item.status)}
                              >
                                {item.status}
                              </Badge>
                            </Text>
                          </Timeline.Item>
                        );
                      })}
                    </Timeline>
                  )}

                  {activities.length > 0 && (
                    <Group justify="center" mt="xl">
                      <Button
                        variant="default"
                        size="xs"
                        disabled={activityPage <= 1}
                        onClick={() => setActivityPage((p) => p - 1)}
                      >
                        Previous
                      </Button>
                      <Text size="sm">Page {activityPage}</Text>
                      <Button
                        variant="default"
                        size="xs"
                        disabled={activities.length < 10}
                        onClick={() => setActivityPage((p) => p + 1)}
                      >
                        Next
                      </Button>
                    </Group>
                  )}
                </div>
              </Paper>
            </Tabs.Panel>

            <Tabs.Panel value="financials" pt="md">
              <Stack gap="lg">
                {/* Wallet Section */}
                <Paper p="md" withBorder radius="md">
                  <Title order={4} mb="md">
                    Wallet & Earnings
                  </Title>
                  <Grid>
                    <Grid.Col span={6}>
                      <Paper p="sm" bg="var(--bg-surface-2)" radius="sm">
                        <Text size="xs" c="dimmed">
                          Total Balance
                        </Text>
                        <Text fw={700} size="xl">
                          ₦{(user.balance || 0).toLocaleString()}
                        </Text>
                      </Paper>
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <Paper p="sm" bg="var(--bg-surface-2)" radius="sm">
                        <Text size="xs" c="dimmed">
                          Available for Withdrawal
                        </Text>
                        <Text fw={700} size="xl" c="green">
                          ₦{(user.availableBalance || 0).toLocaleString()}
                        </Text>
                      </Paper>
                    </Grid.Col>
                  </Grid>

                  {user.bankAccount && (
                    <Stack mt="md" gap="xs">
                      <Text size="sm" fw={500}>
                        Linked Bank Account
                      </Text>
                      <Paper p="sm" withBorder>
                        <Group justify="space-between">
                          <div>
                            <Text fw={500}>{user.bankAccount.bankName}</Text>
                            <Text size="sm">
                              {user.bankAccount.accountName}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {user.bankAccount.accountNumber}
                            </Text>
                          </div>
                          <Badge color="gray">Verified</Badge>
                        </Group>
                      </Paper>
                    </Stack>
                  )}
                </Paper>

                {/* Monetization Section */}
                <Paper p="md" withBorder radius="md">
                  <Title order={4} mb="md">
                    Monetization Status
                  </Title>
                  <Group mb="md">
                    <Text fw={500} size="sm">
                      Current Status:
                    </Text>
                    <Badge
                      color={
                        user.monetizationStatus === "monetized"
                          ? "green"
                          : user.monetizationStatus === "pending"
                            ? "yellow"
                            : user.monetizationStatus === "eligible"
                              ? "blue"
                              : "gray"
                      }
                      size="md"
                    >
                      {user.monetizationStatus === "monetized"
                        ? "Monetized"
                        : user.monetizationStatus === "pending"
                          ? "Pending Review"
                          : user.monetizationStatus === "eligible"
                            ? "Eligible"
                            : user.monetizationStatus === "not_monetized"
                              ? "Not Monetized"
                              : "None"}
                    </Badge>
                  </Group>

                  {user.monetizationProgress && (
                    <Stack gap="xs" mt="sm">
                      <Text size="sm" fw={500}>
                        Current Progress
                      </Text>
                      <Group grow>
                        <Paper p="xs" withBorder bg="var(--bg-surface-2)">
                          <Text size="xs" c="dimmed">
                            Completed Activities
                          </Text>
                          <Text fw={600}>
                            {user.monetizationProgress.activitiesCompleted || 0}
                          </Text>
                        </Paper>
                        <Paper p="xs" withBorder bg="var(--bg-surface-2)">
                          <Text size="xs" c="dimmed">
                            Social Linked (IG & TikTok)
                          </Text>
                          <Badge
                            size="sm"
                            color={
                              user.monetizationProgress.socialLinked
                                ? "green"
                                : "gray"
                            }
                          >
                            {user.monetizationProgress.socialLinked
                              ? "Yes"
                              : "No"}
                          </Badge>
                        </Paper>
                        <Paper p="xs" withBorder bg="var(--bg-surface-2)">
                          <Text size="xs" c="dimmed">
                            Profile Complete
                          </Text>
                          <Badge
                            size="sm"
                            color={
                              user.monetizationProgress.hasCompletedProfile
                                ? "green"
                                : "gray"
                            }
                          >
                            {user.monetizationProgress.hasCompletedProfile
                              ? "Yes"
                              : "No"}
                          </Badge>
                        </Paper>
                      </Group>
                    </Stack>
                  )}
                </Paper>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="badges" pt="md">
              <Paper p="md" withBorder radius="md">
                <Title order={4} mb="md">
                  Earned Badges ({user.badges?.length || 0})
                </Title>
                {(user.badges?.length || 0) === 0 ? (
                  <Text c="dimmed">No badges earned yet.</Text>
                ) : (
                  <Grid>
                    {user.badges?.map((badge: any) => (
                      <Grid.Col span={4} key={badge.id}>
                        <Paper p="sm" withBorder radius="md" bg="var(--bg-surface-2)">
                          <Group align="flex-start" wrap="nowrap">
                            <ThemeIcon
                              size={40}
                              radius="md"
                              color="yellow"
                              variant="light"
                            >
                              <BadgeIcon name={badge.icon} />
                            </ThemeIcon>
                            <div>
                              <Text fw={600} size="sm">
                                {badge.name}
                              </Text>
                              <Text size="xs" c="dimmed" lh={1.2} mb={4}>
                                {badge.description}
                              </Text>
                              {badge.earnedAt && (
                                <Badge size="xs" variant="dot" color="gray">
                                  {new Date(
                                    badge.earnedAt,
                                  ).toLocaleDateString()}
                                </Badge>
                              )}
                            </div>
                          </Group>
                        </Paper>
                      </Grid.Col>
                    ))}
                  </Grid>
                )}
              </Paper>
            </Tabs.Panel>

            <Tabs.Panel value="squad" pt="md">
              {!squadData ? (
                <Paper p="xl" withBorder radius="md">
                  <Center py={60}>
                    <Stack align="center" gap="xs">
                      <ThemeIcon
                        size={60}
                        radius={60}
                        color="gray"
                        variant="light"
                      >
                        <IconUsersGroup size={30} />
                      </ThemeIcon>
                      <Text size="lg" fw={500} c="dimmed">
                        Not in a squad
                      </Text>
                      <Text size="sm" c="dimmed" ta="center" maw={300}>
                        This user is not currently a member of any squad.
                      </Text>
                    </Stack>
                  </Center>
                </Paper>
              ) : (
                <Stack gap="lg">
                  {squadData.myMembershipStatus === "pending_user_acceptance" && (
                    <Alert title="Pending Squad Invitation" color="grape" icon={<IconClock size={16} />}>
                      This user has a pending invitation to join this squad and has not accepted it yet.
                    </Alert>
                  )}
                  {squadData.myMembershipStatus === "pending_admin_approval" && (
                    <Alert title="Pending Join Request" color="orange" icon={<IconClock size={16} />}>
                      This user has a pending request to join this squad and is awaiting approval from the squad leader.
                    </Alert>
                  )}
                  <Grid>
                    <Grid.Col span={{ base: 12, md: 4 }}>
                      <Paper p="md" withBorder radius="md">
                        <Title order={4} mb="md">
                          Squad Details
                        </Title>
                        <Divider mb="sm" />
                        <Stack gap="xs">
                          <Group justify="space-between">
                            <Text size="sm" c="dimmed">Squad Name</Text>
                            <Text fw={500}>{squadData.squad.name}</Text>
                          </Group>
                          <Group justify="space-between">
                            <Text size="sm" c="dimmed">Invite Code</Text>
                            <Text fw={600} ff="monospace">{squadData.squad.inviteCode}</Text>
                          </Group>
                          <Group justify="space-between">
                            <Text size="sm" c="dimmed">User's Role</Text>
                            <Badge color={squadData.role === "leader" ? "yellow" : "blue"}>
                              {squadData.role ? squadData.role.toUpperCase() : "MEMBER"}
                            </Badge>
                          </Group>
                          <Group justify="space-between">
                            <Text size="sm" c="dimmed">Membership Status</Text>
                            <Badge color={squadData.myMembershipStatus === "active" ? "green" : "orange"}>
                              {squadData.myMembershipStatus ? squadData.myMembershipStatus.toUpperCase() : "ACTIVE"}
                            </Badge>
                          </Group>
                          <Group justify="space-between">
                            <Text size="sm" c="dimmed">Created At</Text>
                            <Text size="sm">{new Date(squadData.squad.createdAt).toLocaleDateString()}</Text>
                          </Group>
                        </Stack>
                      </Paper>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 8 }}>
                      <Paper p="md" withBorder radius="md">
                        <Title order={4} mb="md">
                          Squad Earnings (Current Epoch)
                        </Title>
                        <Divider mb="sm" />
                        <Grid>
                          <Grid.Col span={4}>
                            <Paper p="sm" bg="var(--bg-surface-2)" radius="sm">
                              <Text size="xs" c="dimmed">
                                Squad Points Total
                              </Text>
                              <Text fw={700} size="lg">
                                ₦{(squadData.squadPointsTotal || 0).toLocaleString()}
                              </Text>
                            </Paper>
                          </Grid.Col>
                          <Grid.Col span={4}>
                            <Paper p="sm" bg="var(--bg-surface-2)" radius="sm">
                              <Text size="xs" c="dimmed">
                                Weekly Squad Points
                              </Text>
                              <Text fw={700} size="lg">
                                ₦{(squadData.squadPointsThisWeek || 0).toLocaleString()}
                              </Text>
                            </Paper>
                          </Grid.Col>
                          <Grid.Col span={4}>
                            <Paper p="sm" bg="var(--bg-surface-2)" radius="sm">
                              <Text size="xs" c="dimmed">
                                User Contribution
                              </Text>
                              <Text fw={700} size="lg" c="teal">
                                ₦{(squadData.mySquadPointsContribution || 0).toLocaleString()}
                              </Text>
                            </Paper>
                          </Grid.Col>
                        </Grid>
                      </Paper>
                    </Grid.Col>
                  </Grid>

                  <Paper p="md" withBorder radius="md">
                    <Title order={4} mb="md">
                      Squad Members ({squadData.members?.length || 0})
                    </Title>
                    <Table.ScrollContainer minWidth={600}>
                      <Table striped highlightOnHover verticalSpacing="sm">
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Name</Table.Th>
                            <Table.Th>Level</Table.Th>
                            <Table.Th>Role</Table.Th>
                            <Table.Th>Status</Table.Th>
                            <Table.Th>Contribution</Table.Th>
                            <Table.Th>Joined At</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {squadData.members?.map((member: any) => {
                            const statusColor =
                              member.status === "active"
                                ? "green"
                                : member.status === "pending_admin_approval"
                                  ? "orange"
                                  : "gray";
                            return (
                              <Table.Tr key={member.membershipId}>
                                <Table.Td fw={500}>{member.displayName}</Table.Td>
                                <Table.Td>Level {member.level ?? 1}</Table.Td>
                                <Table.Td>
                                  <Badge color={member.role === "leader" ? "yellow" : "blue"} variant="light">
                                    {member.role ? member.role.toUpperCase() : "MEMBER"}
                                  </Badge>
                                </Table.Td>
                                <Table.Td>
                                  <Badge color={statusColor} variant="light">
                                    {member.status ? member.status.replace(/_/g, " ").toUpperCase() : "ACTIVE"}
                                  </Badge>
                                </Table.Td>
                                <Table.Td fw={600}>₦{(member.squadPointsContribution || 0).toLocaleString()}</Table.Td>
                                <Table.Td>{new Date(member.joinedAt).toLocaleDateString()}</Table.Td>
                              </Table.Tr>
                            );
                          })}
                        </Table.Tbody>
                      </Table>
                    </Table.ScrollContainer>
                  </Paper>
                </Stack>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="suspension" pt="md">
              <Stack gap="md">
                {user.isSuspended ? (
                  <Alert color="red" icon={<IconBan size={16} />} title="Account suspended">
                    This user can still sign in, but cannot withdraw funds or complete
                    opportunities until unsuspended.
                  </Alert>
                ) : (
                  <Alert color="blue" icon={<IconCheck size={16} />} title="Account active">
                    Suspended users can still log in, but withdrawals and completing
                    opportunities are blocked. An email and push notification are
                    sent when a suspension is initiated.
                  </Alert>
                )}

                <Paper p="md" withBorder radius="md">
                  <Group justify="space-between" mb="md">
                    <Title order={4}>Suspension Actions</Title>
                    {user.isSuspended ? (
                      <Button
                        color="green"
                        leftSection={<IconCheck size={16} />}
                        onClick={() => {
                          setUnsuspendReason("");
                          setUnsuspendModalOpen(true);
                        }}
                      >
                        Unsuspend User
                      </Button>
                    ) : (
                      <Button
                        color="red"
                        leftSection={<IconBan size={16} />}
                        onClick={() => {
                          setSuspendReason("");
                          setSuspendModalOpen(true);
                        }}
                      >
                        Suspend User
                      </Button>
                    )}
                    <Button
                      color="orange"
                      variant="light"
                      leftSection={<IconArrowBackUp size={16} />}
                      onClick={() => {
                        setClawbackAmount("");
                        setClawbackReason("");
                        setClawbackModalOpen(true);
                      }}
                    >
                      Claw back ₦
                    </Button>
                  </Group>
                  <Text size="sm" c="dimmed">
                    Provide a clear reason when suspending. The user receives an email
                    and push notification with the reason and instructions to contact
                    support.
                  </Text>
                </Paper>

                <Paper p="md" withBorder radius="md" pos="relative">
                  <LoadingOverlay visible={suspensionLoading} />
                  <Title order={4} mb="md">
                    Suspension History
                  </Title>
                  {suspensions.length === 0 ? (
                    <Text c="dimmed">No suspension history recorded.</Text>
                  ) : (
                    <Table.ScrollContainer minWidth={700}>
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Date</Table.Th>
                            <Table.Th>Action</Table.Th>
                            <Table.Th>Reason</Table.Th>
                            <Table.Th>Admin</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {suspensions.map((record) => (
                            <Table.Tr key={record.id}>
                              <Table.Td>
                                {new Date(record.createdAt).toLocaleString()}
                              </Table.Td>
                              <Table.Td>
                                <Badge
                                  color={
                                    record.action === "suspended" ? "red" : "green"
                                  }
                                >
                                  {record.action === "suspended"
                                    ? "Suspended"
                                    : "Unsuspended"}
                                </Badge>
                              </Table.Td>
                              <Table.Td>{record.reason || "—"}</Table.Td>
                              <Table.Td>{record.adminEmail || "—"}</Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </Table.ScrollContainer>
                  )}
                </Paper>
              </Stack>
            </Tabs.Panel>
          </Tabs>
        </Stack>
      )}

      {/* Error Modal */}
      <Modal
        opened={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        title={
          <Group gap="xs">
            <IconX size={20} color="red" />
            <Text fw={700} c="red">
              Error
            </Text>
          </Group>
        }
        centered
        radius="md"
      >
        <Stack gap="md">
          <Text>{errorMessage}</Text>
          <Group justify="flex-end">
            <Button onClick={() => setErrorModalOpen(false)}>Close</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Force Logout Confirmation Modal */}
      <Modal
        opened={forceLogoutModalOpen}
        onClose={() => !forceLogoutLoading && setForceLogoutModalOpen(false)}
        title={
          <Group gap="xs">
            <IconAlertCircle size={20} color="orange" />
            <Text fw={700} c="orange">
              Force Logout
            </Text>
          </Group>
        }
        centered
        radius="md"
        withCloseButton={!forceLogoutLoading}
      >
        <Stack gap="md">
          {forceLogoutSuccess === true ? (
            <>
              <Group gap="xs">
                <ThemeIcon color="green" radius="xl" size="md">
                  <IconCheck size={16} />
                </ThemeIcon>
                <Text size="sm" fw={500} c="green">
                  User forced to log out successfully.
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                All active session tokens have been invalidated and they will be prompted to log in next time they open the app.
              </Text>
              <Group justify="flex-end" mt="xs">
                <Button variant="default" onClick={() => setForceLogoutModalOpen(false)}>
                  Close
                </Button>
              </Group>
            </>
          ) : forceLogoutSuccess === false ? (
            <>
              <Group gap="xs">
                <ThemeIcon color="red" radius="xl" size="md">
                  <IconX size={16} />
                </ThemeIcon>
                <Text size="sm" fw={500} c="red">
                  Failed to force log out user.
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                An error occurred while communicating with the server. Please try again.
              </Text>
              <Group justify="flex-end" gap="sm" mt="xs">
                <Button variant="default" onClick={() => setForceLogoutModalOpen(false)}>
                  Cancel
                </Button>
                <Button color="orange" onClick={triggerForceLogoutRequest}>
                  Retry
                </Button>
              </Group>
            </>
          ) : (
            <>
              <Text size="sm">
                Are you sure you want to force log out this user? All active sessions will be invalidated and they will be forced to log back in.
              </Text>
              <Group justify="flex-end" gap="sm">
                <Button variant="default" onClick={() => setForceLogoutModalOpen(false)} disabled={forceLogoutLoading}>
                  Cancel
                </Button>
                <Button color="orange" loading={forceLogoutLoading} onClick={triggerForceLogoutRequest}>
                  Force Logout
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>

      <Modal
        opened={clawbackModalOpen}
        onClose={() => !clawbackLoading && setClawbackModalOpen(false)}
        title={
          <Group gap="xs">
            <IconArrowBackUp size={20} color="orange" />
            <Text fw={700} c="orange">
              Claw back money
            </Text>
          </Group>
        }
        centered
      >
        <Stack gap="md">
          <Alert color="orange" variant="light">
            Deducts the amount from this user. If it exceeds their balance they go{" "}
            <b>negative (owing)</b> — future earnings clear it first and they
            can't withdraw until it's cleared. For fraud not tied to one proof.
          </Alert>
          <NumberInput
            label="Amount (₦)"
            placeholder="e.g. 500"
            min={1}
            value={clawbackAmount}
            onChange={(v) =>
              setClawbackAmount(typeof v === "number" ? v : "")
            }
            required
          />
          <Textarea
            label="Reason (required)"
            placeholder="Why is this being clawed back?"
            minRows={3}
            value={clawbackReason}
            onChange={(e) => setClawbackReason(e.currentTarget.value)}
            required
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setClawbackModalOpen(false)}
              disabled={clawbackLoading}
            >
              Cancel
            </Button>
            <Button
              color="orange"
              loading={clawbackLoading}
              disabled={!clawbackReason.trim() || !(Number(clawbackAmount) > 0)}
              onClick={handleClawback}
            >
              Claw back
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={suspendModalOpen}
        onClose={() => !suspendActionLoading && setSuspendModalOpen(false)}
        title={
          <Group gap="xs">
            <IconBan size={20} color="red" />
            <Text fw={700} c="red">
              Suspend User
            </Text>
          </Group>
        }
        centered
        radius="md"
        withCloseButton={!suspendActionLoading}
      >
        <Stack gap="md">
          <Text size="sm">
            Suspend {user?.email}? The user will receive an email and push
            notification and will be blocked from withdrawals and completing
            opportunities.
          </Text>
          <Textarea
            label="Reason for suspension"
            placeholder="Explain why this account is being suspended..."
            minRows={4}
            required
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.currentTarget.value)}
          />
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setSuspendModalOpen(false)}
              disabled={suspendActionLoading}
            >
              Cancel
            </Button>
            <Button
              color="red"
              loading={suspendActionLoading}
              disabled={!suspendReason.trim()}
              onClick={handleSuspendUser}
            >
              Suspend User
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={unsuspendModalOpen}
        onClose={() => !suspendActionLoading && setUnsuspendModalOpen(false)}
        title={
          <Group gap="xs">
            <IconCheck size={20} color="green" />
            <Text fw={700} c="green">
              Unsuspend User
            </Text>
          </Group>
        }
        centered
        radius="md"
        withCloseButton={!suspendActionLoading}
      >
        <Stack gap="md">
          <Text size="sm">
            Restore full access for {user?.email}? Withdrawals and opportunities will
            be available again.
          </Text>
          <Textarea
            label="Note (optional)"
            placeholder="Optional internal note for the unsuspension..."
            minRows={3}
            value={unsuspendReason}
            onChange={(e) => setUnsuspendReason(e.currentTarget.value)}
          />
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setUnsuspendModalOpen(false)}
              disabled={suspendActionLoading}
            >
              Cancel
            </Button>
            <Button
              color="green"
              loading={suspendActionLoading}
              onClick={handleUnsuspendUser}
            >
              Unsuspend User
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default UserDetails;
